import { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login({ onLogin }) {
  const [mode, setMode] = useState('login'); // login | register | reset
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });
  const [resetData, setResetData] = useState({ email: '', code: '', new_password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        const res = await axios.post(`${API}/auth/login`, { email: formData.email, password: formData.password });
        toast.success('Login successful!');
        onLogin(res.data.token, res.data.user, res.data.permissions, res.data.allowed_stores);
      } else if (mode === 'register') {
        const res = await axios.post(`${API}/auth/register`, { email: formData.email, password: formData.password, name: formData.name });
        toast.success('Registration successful! You have been assigned a default role. Contact admin for role changes.');
        onLogin(res.data.token, res.data.user, res.data.permissions, res.data.allowed_stores);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally { setLoading(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, resetData);
      toast.success('Password reset successfully! You can now login.');
      setMode('login');
      setResetData({ email: '', code: '', new_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-white relative">
      {/* Left brand panel — black with gold accent */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0A0A0A] flex-col justify-between p-12 relative overflow-hidden">
        {/* subtle grain via gradient */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #fff 1px, transparent 1px), radial-gradient(circle at 80% 70%, #fff 1px, transparent 1px)', backgroundSize: '24px 24px, 32px 32px' }} />
        <div className="relative">
          <h1 className="brand-mark text-white text-5xl tracking-wider leading-none">SAMUDRIKA</h1>
          <div className="flex items-center gap-3 mt-3">
            <span className="h-px w-10 bg-[#C9A961]" />
            <p className="label-eyebrow text-white/60">Silver &amp; Jewellery CRM</p>
          </div>
        </div>
        <div className="relative">
          <p className="display-heading text-white text-4xl xl:text-5xl leading-tight max-w-md" style={{ fontFamily: "'Anton', 'Bebas Neue', sans-serif" }}>
            ONE PLATFORM<br />
            FOR YOUR ENTIRE<br />
            <span className="text-[#C9A961]">JEWELLERY BUSINESS.</span>
          </p>
          <p className="text-white/50 text-sm mt-6 max-w-sm">Inventory, daybook, vendor balance, profit &amp; loss, branch operations — unified.</p>
        </div>
        <div className="relative flex items-center gap-3 text-white/40 text-xs">
          <span className="label-eyebrow">CREATED BY</span>
          <span className="text-[#C9A961] brand-mark text-base tracking-widest">SARAN</span>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 lg:px-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden text-center mb-8">
            <h1 className="brand-mark text-3xl tracking-wider text-[#0A0A0A]" data-testid="login-heading">SAMUDRIKA</h1>
            <p className="label-eyebrow mt-2 text-[#6B7280]">Silver &amp; Jewellery CRM</p>
          </div>

          {mode === 'reset' ? (
            <div data-testid="reset-card">
              <p className="label-eyebrow text-[#C9A961]">Recover Access</p>
              <h2 className="display-heading text-4xl mt-2 mb-1">Reset Password</h2>
              <p className="text-sm text-[#6B7280] mb-8">Enter the reset code provided by your admin.</p>
              <form onSubmit={handleReset} className="space-y-5">
                <div>
                  <Label htmlFor="reset-email" className="label-eyebrow">Email</Label>
                  <Input id="reset-email" data-testid="reset-email-input" type="email"
                    value={resetData.email} onChange={e => setResetData({...resetData, email: e.target.value})}
                    required className="mt-2 h-11 border-[#E5E2DA] focus-visible:ring-[#C9A961]" placeholder="Your registered email" />
                </div>
                <div>
                  <Label htmlFor="reset-code" className="label-eyebrow">Reset Code</Label>
                  <Input id="reset-code" data-testid="reset-code-input"
                    value={resetData.code} onChange={e => setResetData({...resetData, code: e.target.value})}
                    required className="mt-2 h-11 font-mono tracking-wider border-[#E5E2DA] focus-visible:ring-[#C9A961]" placeholder="e.g. A1B2C3D4" />
                  <p className="text-xs text-[#6B7280] mt-1.5">Ask your admin to generate a reset code from Users panel.</p>
                </div>
                <div>
                  <Label htmlFor="new-password" className="label-eyebrow">New Password</Label>
                  <Input id="new-password" data-testid="reset-password-input" type="password"
                    value={resetData.new_password} onChange={e => setResetData({...resetData, new_password: e.target.value})}
                    required minLength={6} className="mt-2 h-11 border-[#E5E2DA] focus-visible:ring-[#C9A961]" placeholder="Min 6 characters" />
                </div>
                <Button type="submit" data-testid="reset-submit-button"
                  className="w-full h-11 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-white font-semibold tracking-wide" disabled={loading}>
                  {loading ? 'RESETTING...' : 'RESET PASSWORD'}
                </Button>
              </form>
              <div className="mt-6 text-center">
                <button type="button" data-testid="back-to-login-button" onClick={() => setMode('login')}
                  className="text-sm text-[#0A0A0A] hover:text-[#C9A961] transition-colors font-medium">← Back to Sign In</button>
              </div>
            </div>
          ) : (
            <div data-testid="login-card">
              <p className="label-eyebrow text-[#C9A961]">{mode === 'login' ? 'Welcome Back' : 'Get Started'}</p>
              <h2 className="display-heading text-4xl mt-2 mb-1">{mode === 'login' ? 'Sign In' : 'Create Account'}</h2>
              <p className="text-sm text-[#6B7280] mb-8">
                {mode === 'login' ? 'Enter your credentials to access your account.' : 'Register to get started. Admin will assign your role.'}
              </p>
              <form onSubmit={handleSubmit} className="space-y-5">
                {mode === 'register' && (
                  <div>
                    <Label htmlFor="name" className="label-eyebrow">Full Name</Label>
                    <Input id="name" data-testid="register-name-input" type="text"
                      value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}
                      required className="mt-2 h-11 border-[#E5E2DA] focus-visible:ring-[#C9A961]" />
                  </div>
                )}
                <div>
                  <Label htmlFor="email" className="label-eyebrow">Email</Label>
                  <Input id="email" data-testid="login-email-input" type="email"
                    value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})}
                    required className="mt-2 h-11 border-[#E5E2DA] focus-visible:ring-[#C9A961]" />
                </div>
                <div>
                  <Label htmlFor="password" className="label-eyebrow">Password</Label>
                  <Input id="password" data-testid="login-password-input" type="password"
                    value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})}
                    required className="mt-2 h-11 border-[#E5E2DA] focus-visible:ring-[#C9A961]" />
                </div>
                <Button type="submit" data-testid="login-submit-button"
                  className="w-full h-11 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-white font-semibold tracking-wide" disabled={loading}>
                  {loading ? 'PROCESSING...' : (mode === 'login' ? 'SIGN IN' : 'REGISTER')}
                </Button>
              </form>
              <div className="mt-6 flex flex-col items-center gap-3">
                <button type="button" data-testid="toggle-auth-mode-button"
                  onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
                  className="text-sm text-[#0A0A0A] hover:text-[#C9A961] transition-colors font-medium">
                  {mode === 'login' ? "Don't have an account? Register" : 'Already have an account? Sign in'}
                </button>
                {mode === 'login' && (
                  <button type="button" data-testid="forgot-password-button"
                    onClick={() => setMode('reset')}
                    className="text-xs text-[#6B7280] hover:text-[#0A0A0A] transition-colors">Forgot password?</button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
